import { useMachine } from '@xstate/react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import {
  SuccessNotification,
  SuccessNotificationProps,
} from '../core/screens/SuccessNotification/SuccessNotification';
import { applicationParameters } from '../utilities/application-parameters';
import { messages as authorizationDirectorMessages } from '../components/AuthorizationDirector/messages';
import { AccessCodeResult } from '../core/components/ConnectAccounts/AccessCodeResult';
import { LoadingOverlay } from '../core/screens/LoadingOverlay/LoadingOverlay';
import { messages as awaitCredentialsMessages } from '../components/screens/AwaitingCredentialsScreen/messages';
import { CredentialsEntity, CredentialsStatus } from '../models/credentials';
import { ReduxCredentials } from '../components/screens/SubmitCredentials/ReduxCredentials';
import { CredentialsResult } from '../components/CredentialsDirector/CredentialsResult';
import { ProviderEntity } from '../models/provider';
import { RefreshableItem } from '../models/refreshableItem';
import { createProductsMachine } from './models/productsMachine';
import { mapProductsToConditionalScope } from './models/products';

export type ProductsMachineViewProps = {
  isPermanentUser: boolean;
  shouldRedirectEarly: boolean;
  credentialsId?: CredentialsEntity<CredentialsStatus.UPDATED>['id'];
  refreshableItems: Array<RefreshableItem>;
  SuccessNotificationComponent?: React.ComponentType<SuccessNotificationProps>;
  provider: ProviderEntity;
  onCancel: () => void;
};

// istanbul ignore next: this component is a switch.src/Products/ProductsMachine.typegen.ts
export const ProductsMachineView: React.FC<ProductsMachineViewProps> = ({
  isPermanentUser,
  shouldRedirectEarly,
  credentialsId,
  refreshableItems,
  SuccessNotificationComponent = SuccessNotification,
  provider,
  onCancel,
}) => {
  const history = useHistory();
  const machine = useMemo(() => {
    const scope = applicationParameters.get('scope');
    const redirectUri = applicationParameters.get('redirectUri');
    const clientId = applicationParameters.get('clientId');
    const state = applicationParameters.get('state');
    const products = applicationParameters.get('products');

    return createProductsMachine({
      isPermanentUser,
      authorizePayload: {
        scope,
        redirectUri,
        clientId,
        state,
        conditionalScope: mapProductsToConditionalScope(products),
      },
    });
  }, [isPermanentUser]);

  const [state, send] = useMachine(machine);
  const { authorizeResponse, error } = state.context;

  if (state.matches('auth')) {
    return (
      <ReduxCredentials
        onBack={() => {
          onCancel();
          // ReduxCredentials doesn't reliably call the onBack callback,
          // instead sometimes doing history.goBack by itself, so we're doing
          // the same to align until we fix the issue.
          // TODO: use send() to manage the navigation in XState
          history.goBack();
        }}
        provider={provider}
        refreshableItems={refreshableItems}
        onDone={() => send('CREDENTIALS')}
        waitForUpdated={!shouldRedirectEarly}
      />
    );
  }

  if (state.matches('error')) {
    throw error;
  }

  if (state.matches('success')) {
    const applicationSuccessResult = isPermanentUser
      ? new CredentialsResult({
          credentialsId: credentialsId!,
        })
      : new AccessCodeResult({
          accessCode: authorizeResponse!.code,
          credentialsId: credentialsId!,
        });

    return (
      <SuccessNotificationComponent
        applicationSuccessResult={applicationSuccessResult}
        overlayHeading={
          <FormattedMessage
            {...authorizationDirectorMessages.accountConnectedTo}
            values={{ providerName: provider?.displayName }}
          />
        }
      />
    );
  }

  // waiting for login
  return (
    <LoadingOverlay
      text={
        <FormattedMessage
          {...awaitCredentialsMessages.connectingTo}
          values={{ providerName: provider?.displayName }}
        />
      }
    />
  );
};
